/**
 * NetworkCardsSection — "Explore the SocialPulse Network".
 *
 * Ships 6 featured platform cards + a "View Entire Network" tail
 * link so the homepage doesn't have to dump the whole SocialPulse
 * portfolio on visitors at once.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Utensils, Plane, HomeIcon, Sprout,
  GraduationCap, Users2, BookOpen, HeartPulse,
  Landmark, TreePine, Wallet, PlaneTakeoff,
} from "lucide-react";

const NETWORK = [
  {
    key: "foodpulse", label: "FoodPulse", tone: "bg-orange-500",
    tag: "Food & Culinary", blurb: "Local flavors, recipes and food stories.",
    icon: Utensils, to: "https://foodpulse.net", external: true,
  },
  {
    key: "travelerpulse", label: "TravelerPulse", tone: "bg-sky-500",
    tag: "Travel & Places", blurb: "Trips, itineraries and place discovery.",
    icon: Plane, to: "/travel",
  },
  {
    key: "homespulse", label: "HomesPulse", tone: "bg-purple-500",
    tag: "Home & Living", blurb: "Housing, neighborhoods and home tips.",
    icon: HomeIcon, to: "/homes",
  },
  {
    key: "naturepulse", label: "NaturePulse", tone: "bg-emerald-500",
    tag: "Nature & Environment", blurb: "Environment, sustainability, wildlife.",
    icon: TreePine, to: "/nature",
  },
  {
    key: "alumnipulse", label: "AlumniPulse", tone: "bg-amber-500",
    tag: "Alumni & Networks", blurb: "Reconnect classmates and school communities.",
    icon: GraduationCap, to: "/alumni",
  },
  {
    key: "familytree", label: "FamilyTree", tone: "bg-rose-500",
    tag: "Family & Heritage", blurb: "Build your family tree with kin.",
    icon: Users2, to: "/indian-family-tree",
  },
];

const HIDDEN = [
  { label: "FarmersPulse",     to: "/weather/farmer",     icon: Sprout },
  { label: "HealthPulse",      to: "/health",             icon: HeartPulse },
  { label: "EducationPulse",   to: "/education",          icon: GraduationCap },
  { label: "FinancePulse",     to: "/finance",            icon: Wallet },
  { label: "AirlinesAirports", to: "/airlines-airports",  icon: PlaneTakeoff },
  { label: "SocialPulse.blog", to: "https://socialpulse.blog", icon: BookOpen, external: true },
];

function LinkOrAnchor({ to, external, children, className, ...rest }) {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {children}
      </a>
    );
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
}

export default function NetworkCardsSection() {
  return (
    <section
      className="w-full"
      style={{ background: "#FDFBF7" }}
      data-testid="home-network-cards"
    >
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-14">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase text-xs font-mono font-semibold tracking-[0.2em] text-[#50586C] mb-2">
            One family of platforms
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1226] tracking-tight leading-snug">
            Explore the SocialPulse Network
          </h2>
          <p className="text-base text-[#50586C] mt-1.5">
            Each platform tuned to a corner of community life.
          </p>
        </div>
        <details className="group" data-testid="home-network-viewall">
          <summary className="list-none cursor-pointer text-sm font-semibold text-[#1E64C8] hover:text-[#164FA0] inline-flex items-center gap-0.5">
            View Entire Network <ArrowRight className="w-3.5 h-3.5 group-open:rotate-90 transition-transform" />
          </summary>
          <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {HIDDEN.map((h) => (
              <li key={h.label}>
                <LinkOrAnchor
                  to={h.to}
                  external={h.external}
                  className="inline-flex items-center gap-2 rounded-full bg-white shadow-sm hover:shadow px-3 py-1.5 text-xs font-semibold text-[#0B1226]"
                  data-testid={`home-network-hidden-${h.label.toLowerCase().replace(/\./g, "-")}`}
                >
                  <h.icon className="w-3.5 h-3.5 text-[#1E64C8]" />
                  {h.label}
                </LinkOrAnchor>
              </li>
            ))}
          </ul>
        </details>
      </header>

      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {NETWORK.map((n) => (
          <li key={n.key} data-testid={`home-network-card-${n.key}`}>
            <LinkOrAnchor
              to={n.to}
              external={n.external}
              className="group block h-full rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 overflow-hidden"
            >
              <div className={`h-20 w-full ${n.tone} relative flex items-center px-4 gap-2.5`}>
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/25 text-white">
                  <n.icon className="w-5 h-5" />
                </span>
                <span className="text-white font-semibold text-sm leading-tight">{n.label}</span>
              </div>
              <div className="p-4">
                <div className="uppercase text-[10px] font-mono font-semibold tracking-[0.15em] text-[#50586C]">{n.tag}</div>
                <p className="mt-1 text-sm text-[#1F2333] leading-snug">{n.blurb}</p>
              </div>
            </LinkOrAnchor>
          </li>
        ))}
      </ul>
      </div>
    </section>
  );
}
